import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Database, Search, FileText, ChevronRight, Zap, Play, FastForward, Settings, Layout, User, Plus, Download, Globe, PlayCircle } from 'lucide-react';

export default function CandorMigrationShowcase({ onOpenDownload, onOpenLogin, onOpenContact, onOpenDemo, onOpenSandbox }) {
  const [visibleLines, setVisibleLines] = useState(0);
  const [showInsights, setShowInsights] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [activeConstruct, setActiveConstruct] = useState('paging');
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const sourceCodeLines = [
    <><span className="text-blue-600 font-medium">CREATE PROCEDURE</span> dbo.usp_GetCustomerOrders</>,
    <>  @CustomerID <span className="text-blue-600 font-medium">INT</span>,</>,
    <>  @StartDate <span className="text-blue-600 font-medium">DATE</span>,</>,
    <>  @EndDate <span className="text-blue-600 font-medium">DATE</span></>,
    <><span className="text-blue-600 font-medium">AS</span></>,
    <></>,
    <><span className="text-blue-600 font-medium">SELECT</span></>,
    <>    o.OrderID,</>,
    <>    o.OrderDate,</>,
    <>    o.TotalAmount,</>,
    <>    c.CustomerName,</>,
    <>    c.Email</>,
    <><span className="text-blue-600 font-medium">FROM</span> dbo.Orders o</>,
    <><span className="text-blue-600 font-medium">INNER JOIN</span> dbo.Customers c</>,
    <>  <span className="text-blue-600 font-medium">ON</span> o.CustomerID = c.CustomerID</>,
    <><span className="text-blue-600 font-medium">WHERE</span> o.CustomerID = @CustomerID</>,
    <>  <span className="text-blue-600 font-medium">AND</span> o.OrderDate <span className="text-blue-600 font-medium">BETWEEN</span> @StartDate <span className="text-blue-600 font-medium">AND</span> @EndDate</>,
    <><span className="text-blue-600 font-medium">ORDER BY</span> o.OrderDate <span className="text-blue-600 font-medium">DESC</span>;</>
  ];

  const targetCodeLines = [
    <><span className="text-blue-600 font-medium">CREATE OR REPLACE PROCEDURE</span></>,
    <>  GET_CUSTOMER_ORDERS(</>,
    <>    CUSTOMER_ID <span className="text-emerald-600 font-medium">NUMBER</span>,</>,
    <>    START_DATE <span className="text-emerald-600 font-medium">DATE</span>,</>,
    <>    END_DATE <span className="text-emerald-600 font-medium">DATE</span></>,
    <>  )</>,
    <><span className="text-blue-600 font-medium">RETURNS TABLE</span> (</>,
    <>    ORDER_ID <span className="text-emerald-600 font-medium">NUMBER</span>,</>,
    <>    ORDER_DATE <span className="text-emerald-600 font-medium">DATE</span>,</>,
    <>    TOTAL_AMOUNT <span className="text-emerald-600 font-medium">NUMBER</span>(18,2),</>,
    <>    CUSTOMER_NAME <span className="text-emerald-600 font-medium">VARCHAR</span>,</>,
    <>    EMAIL <span className="text-emerald-600 font-medium">VARCHAR</span></>,
    <>  )</>,
    <><span className="text-blue-600 font-medium">LANGUAGE</span> SQL</>,
    <><span className="text-blue-600 font-medium">AS</span></>,
    <>$$</>,
    <>  <span className="text-blue-600 font-medium">SELECT</span></>,
    <>    o.ORDER_ID,</>,
    <>    o.ORDER_DATE,</>,
    <>    o.TOTAL_AMOUNT,</>,
    <>    c.CUSTOMER_NAME,</>,
    <>    c.EMAIL</>,
    <><span className="text-blue-600 font-medium">FROM</span> dbo.ORDERS o</>
  ];

  // Infinite loop animation logic
  useEffect(() => {
    let interval;
    let timeout;
    let currentLine = 0;

    const startLoop = () => {
      setIsMigrating(true);
      setVisibleLines(0);
      setShowInsights(false);
      currentLine = 0;
      
      timeout = setTimeout(() => {
        interval = setInterval(() => {
          currentLine++;
          setVisibleLines(currentLine);
          
          if (currentLine >= targetCodeLines.length) {
            clearInterval(interval);
            setTimeout(() => {
              setShowInsights(true);
              setIsMigrating(false);
              
              // Wait 5 seconds, then restart the animation
              timeout = setTimeout(startLoop, 5000);
            }, 300);
          }
        }, 90); // 90ms per line feels like a real editor
      }, 500);
    };

    startLoop();

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Auto-play for dialect construct tiles
  useEffect(() => {
    if (!isAutoPlaying) return;

    const constructs = ['paging', 'datetime', 'null', 'temp', 'identifiers', 'auto'];
    const interval = setInterval(() => {
      setActiveConstruct(prev => {
        const currentIndex = constructs.indexOf(prev);
        const nextIndex = (currentIndex + 1) % constructs.length;
        return constructs[nextIndex];
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handleMouseEnter = (construct) => {
    setIsAutoPlaying(false);
    setActiveConstruct(construct);
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };
  return (
    <div className="bg-white">
      {/* HERO SECTION */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          
          {/* LEFT SIDE: "Why Candor?" Comparison Table */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-8 flex flex-col justify-between h-full relative overflow-hidden">
            {/* Top decorative accent gradient */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500" />
            
            <div>
              <div className="mb-6">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                  Why Candor?
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">
                  How Candor outperforms general AI coding assistants for database migrations
                </p>
              </div>

              {/* Comparison Table */}
              <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                <table className="w-full text-left border-collapse min-w-[640px]">
                  <thead>
                    <tr className="bg-slate-900 text-white text-[10px] sm:text-xs uppercase tracking-wider">
                      <th className="py-3 px-3 font-bold border-b border-slate-800">Capability Layer</th>
                      <th className="py-3 px-3 font-bold text-emerald-400 border-b border-slate-800 bg-slate-800/90">
                        Candor (Desktop IDE &amp; Web)
                      </th>
                      <th className="py-3 px-3 font-bold text-slate-300 border-b border-slate-800">
                        Browser AI (ChatGPT / Claude)
                      </th>
                      <th className="py-3 px-3 font-bold text-slate-300 border-b border-slate-800">
                        Code Editors (Cursor / Claude / Copilot)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 text-[11px] sm:text-xs text-slate-700 font-medium">
                    <tr className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-3 font-bold text-slate-900 bg-slate-50/60">Schema Awareness</td>
                      <td className="py-3 px-3 font-semibold text-emerald-700 bg-emerald-50/40">🔌 Live Dictionary Introspection</td>
                      <td className="py-3 px-3 text-slate-500">❌ Completely Blind</td>
                      <td className="py-3 px-3 text-slate-500">❌ Static File Only</td>
                    </tr>
                    <tr className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-3 font-bold text-slate-900 bg-slate-50/60">Zero-Shot Training</td>
                      <td className="py-3 px-3 font-semibold text-emerald-700 bg-emerald-50/40">📈 DDL Graph Grounding</td>
                      <td className="py-3 px-3 text-slate-500">❌ Manual Copy-Paste Loop</td>
                      <td className="py-3 px-3 text-amber-800">⚠️ Basic Context Window</td>
                    </tr>
                    <tr className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-3 font-bold text-slate-900 bg-slate-50/60">No AI Hallucination</td>
                      <td className="py-3 px-3 font-semibold text-emerald-700 bg-emerald-50/40">🏗️ Deterministic DDL Verification</td>
                      <td className="py-3 px-3 text-slate-500">❌ High Guesswork Risk</td>
                      <td className="py-3 px-3 text-slate-500">❌ Blind Structural Guessing</td>
                    </tr>
                    <tr className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-3 font-bold text-slate-900 bg-slate-50/60">Environment Isolation</td>
                      <td className="py-3 px-3 font-semibold text-emerald-700 bg-emerald-50/40">🛡️ Air-Gapped Local Compute*</td>
                      <td className="py-3 px-3 text-slate-500">❌ Public Ingestion Exposure</td>
                      <td className="py-3 px-3 text-amber-800">⚠️ Cloud Bound Dependency</td>
                    </tr>
                    <tr className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-3 font-bold text-slate-900 bg-slate-50/60">Inference Cost Economics</td>
                      <td className="py-3 px-3 font-semibold text-emerald-700 bg-emerald-50/40">🔑 Zero-Markup BYOK / 100% Free Local</td>
                      <td className="py-3 px-3 text-slate-500">❌ Fixed Cost Premium Subscription</td>
                      <td className="py-3 px-3 text-slate-500">❌ Fixed Cost Premium Subscription</td>
                    </tr>
                    <tr className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-3 font-bold text-slate-900 bg-slate-50/60">Deployment Footprint</td>
                      <td className="py-3 px-3 font-semibold text-emerald-700 bg-emerald-50/40">🌐 Hybrid Desktop &amp; Web IDE</td>
                      <td className="py-3 px-3 text-slate-500">💻 Browser-Only Window</td>
                      <td className="py-3 px-3 text-slate-500">🖥️ Desktop-Only Binary</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col gap-2 text-xs text-slate-500">
              <p className="text-[11px] text-slate-500">
                * Air-gapped local compute available with supported local model runtimes and Community / Enterprise deployment options.
              </p>
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-600">Built specifically for enterprise DBAs &amp; Data Engineers</span>
                <span className="font-bold text-blue-600 flex items-center gap-1">
                  <Zap className="w-3.5 h-3.5 fill-current" /> Schema-Aware Engine
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE TEXT */}
          <div className="lg:pl-4">
            <div className="inline-block px-3 py-1.5 mb-6 rounded-full bg-blue-50 text-blue-600 text-xs font-bold tracking-widest uppercase border border-blue-100">
              Enterprise Dialect Migrations
            </div>
            
            <h1 className="text-4xl md:text-[40px] leading-[1.1] font-extrabold text-[#111827] mb-4 tracking-tight text-balance max-w-[600px]">
              Convert 100+ Legacy SQL Server Scripts to Oracle Instantly Without Hallucinations.
            </h1>
            
            <p className="text-[18px] sm:text-[20px] text-[#4B5563] leading-relaxed mb-10 font-medium">
              Stop manually rewriting T-SQL identities, datetime functions, temporary tables, and proprietary SQL Server syntax for Oracle. Candor reads your source and target schemas simultaneously using live catalogs or imported DDL to produce metadata-aware T-SQL → Oracle transformations before a single line is rewritten.
            </p>
            
            <div className="flex flex-nowrap items-center gap-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 sm:overflow-visible">
              <button 
                onClick={onOpenSandbox || onOpenDownload}
                className="px-3 py-2.5 bg-[#0D7A54] hover:bg-[#0A6343] text-white font-bold rounded-lg shadow-sm transition-colors text-[13px] whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
              >
                <Zap className="w-4 h-4 fill-current" /> Try Sandbox
              </button>
              <button 
                onClick={onOpenDownload}
                className="px-3 py-2.5 bg-[#2563EB] hover:bg-blue-700 text-white font-bold rounded-lg shadow-sm transition-colors text-[13px] whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
              >
                <Download className="w-4 h-4" /> Download IDE
              </button>
              <button 
                onClick={onOpenLogin}
                className="px-3 py-2.5 bg-white text-[#1E3A8A] font-bold rounded-lg border border-gray-300 hover:border-[#2563EB] hover:text-[#2563EB] shadow-sm transition-colors text-[13px] whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
              >
                <Globe className="w-4 h-4" /> Open Web IDE
              </button>
              <button 
                onClick={onOpenDemo || onOpenContact}
                className="px-3 py-2.5 bg-[#4F39C2] hover:bg-[#402da1] text-white font-bold rounded-lg shadow-sm transition-colors text-[13px] whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
              >
                <PlayCircle className="w-4 h-4" /> Watch Demo
              </button>
            </div>
            
            <div className="mb-8">
              <Link to="/features" className="text-[#1D4ED8] font-bold hover:underline text-[15px] inline-flex items-center">
                Explore all Features <span aria-hidden="true" className="ml-1">→</span>
              </Link>
            </div>

            <div className="text-[13px] text-gray-500 font-medium leading-relaxed max-w-[550px]">
              Supports live connections or imported DDL · Multi-document workspace · SQL Server · Oracle · PostgreSQL · Snowflake · MySQL · Databricks
            </div>
          </div>
        </div>
      </div>

      {/* L2 — ENGINES STRIP */}
      <div className="border-t border-b border-gray-200 bg-gray-50/50 py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-5">
            <span className="text-[11px] font-extrabold text-gray-500 tracking-[0.2em] uppercase">
              Natively compatible with leading data engines
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="px-5 py-2.5 bg-white border-2 border-blue-200 text-blue-800 font-bold rounded-full text-sm shadow-sm flex items-center gap-2">
               <Database className="w-4 h-4 text-blue-600 shrink-0" /> Microsoft SQL Server
            </div>
            <div className="px-5 py-2.5 bg-white border-2 border-red-200 text-red-700 font-bold rounded-full text-sm shadow-sm flex items-center gap-2">
               <Database className="w-4 h-4 text-red-600 shrink-0" /> Oracle
            </div>
            <div className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-full text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 cursor-default">
               <Database className="w-4 h-4 text-[#336791] shrink-0" /> PostgreSQL
            </div>
            <div className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-full text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 cursor-default">
               <Database className="w-4 h-4 text-[#29B5E8] shrink-0" /> Snowflake
            </div>
            <div className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-full text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 cursor-default">
               <Database className="w-4 h-4 text-[#E48E00] shrink-0" /> MySQL
            </div>
            <div className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-full text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 cursor-default">
               <Database className="w-4 h-4 text-[#FF3621] shrink-0" /> Databricks
            </div>
            <div className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-full text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 cursor-default">
               <Database className="w-4 h-4 text-[#003B57] shrink-0" /> SQLite
            </div>
            <div className="px-5 py-2.5 bg-gray-100 border border-gray-200 text-gray-700 font-bold rounded-full text-sm">+ Connection Manager</div>
          </div>
        </div>
      </div>

      {/* L3 — MINI DIALECT SHOWCASE */}
      <div className="bg-white py-12">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-block px-3 py-1 mb-4 rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold tracking-[0.15em] uppercase border border-indigo-100">
              See the convert — not the slogan
            </div>
            <h2 className="text-[36px] md:text-[42px] leading-[1.2] font-extrabold text-gray-900 mb-6 tracking-tight">
              T-SQL → Oracle with schema-aware dialect mapping
            </h2>
              <p className="text-[18px] text-gray-600 leading-relaxed font-medium">
              A compact preview of what DBAs and data engineers open the page to verify.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden mb-8">
            <div className="flex flex-col md:flex-row items-stretch border-b border-gray-200">
              
              {/* Left Panel */}
              <div className="flex-1 border-r border-gray-200 bg-[#FAFAFA] min-w-0">
                <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
                  <span className="text-[11px] font-bold text-gray-500 tracking-wider">INPUT: MS SQL SERVER T-SQL</span>
                  <span className="text-xs font-mono text-gray-400">source_sqlserver.sql</span>
                </div>
                <div className="p-6 font-mono text-[13px] leading-[1.6] overflow-x-auto text-gray-800">
<pre>
<span className="text-blue-600 font-semibold">SELECT</span> <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'paging' ? 'bg-amber-200 text-amber-900 font-bold' : 'text-blue-600 font-semibold'}`} onMouseEnter={() => handleMouseEnter('paging')} onMouseLeave={handleMouseLeave}>TOP 100</span>
    <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'identifiers' ? 'bg-amber-200 text-amber-900 font-bold' : ''}`} onMouseEnter={() => handleMouseEnter('identifiers')} onMouseLeave={handleMouseLeave}>[user_id]</span>,
    <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'null' ? 'bg-amber-200 text-amber-900 font-bold' : 'text-purple-600'}`} onMouseEnter={() => handleMouseEnter('null')} onMouseLeave={handleMouseLeave}>ISNULL</span>(<span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'identifiers' ? 'bg-amber-200 text-amber-900 font-bold' : ''}`} onMouseEnter={() => handleMouseEnter('identifiers')} onMouseLeave={handleMouseLeave}>[status]</span>, 'Active'),
    <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'datetime' ? 'bg-amber-200 text-amber-900 font-bold' : 'text-red-500 line-through decoration-red-500/50'}`} onMouseEnter={() => handleMouseEnter('datetime')} onMouseLeave={handleMouseLeave}>GETDATE()</span> AS <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'identifiers' ? 'bg-amber-200 text-amber-900 font-bold' : ''}`} onMouseEnter={() => handleMouseEnter('identifiers')} onMouseLeave={handleMouseLeave}>[ts]</span>
<span className="text-blue-600 font-semibold">INTO</span> <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'temp' ? 'bg-amber-200 text-amber-900 font-bold' : 'text-amber-600'}`} onMouseEnter={() => handleMouseEnter('temp')} onMouseLeave={handleMouseLeave}>#temp_users</span>
<span className="text-blue-600 font-semibold">FROM</span> <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'identifiers' ? 'bg-amber-200 text-amber-900 font-bold' : ''}`} onMouseEnter={() => handleMouseEnter('identifiers')} onMouseLeave={handleMouseLeave}>[production].[dbo].[users]</span>;
</pre>
                </div>
              </div>

              {/* Right Panel */}
              <div className="flex-1 bg-white min-w-0">
                <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
                  <span className="text-[11px] font-bold text-gray-500 tracking-wider text-emerald-600">AUTOMATED OUTPUT: ORACLE SQL</span>
                  <span className="text-xs font-mono text-gray-400">target_oracle.sql</span>
                </div>
                <div className="p-6 font-mono text-[13px] leading-[1.6] overflow-x-auto text-gray-800">
<pre>
<span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'temp' ? 'bg-emerald-200 text-emerald-900 font-bold' : 'text-emerald-600 font-semibold'}`} onMouseEnter={() => handleMouseEnter('temp')} onMouseLeave={handleMouseLeave}>CREATE GLOBAL TEMPORARY TABLE</span> temp_users
<span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'temp' ? 'bg-emerald-200 text-emerald-900 font-bold' : 'text-emerald-600 font-semibold'}`} onMouseEnter={() => handleMouseEnter('temp')} onMouseLeave={handleMouseLeave}>ON COMMIT PRESERVE ROWS AS</span>
<span className="text-blue-600 font-semibold">SELECT</span>
    <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'identifiers' ? 'bg-emerald-200 text-emerald-900 font-bold' : ''}`} onMouseEnter={() => handleMouseEnter('identifiers')} onMouseLeave={handleMouseLeave}>user_id</span>,
    <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'null' ? 'bg-emerald-200 text-emerald-900 font-bold' : 'text-emerald-600 font-semibold'}`} onMouseEnter={() => handleMouseEnter('null')} onMouseLeave={handleMouseLeave}>NVL</span>(<span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'identifiers' ? 'bg-emerald-200 text-emerald-900 font-bold' : ''}`} onMouseEnter={() => handleMouseEnter('identifiers')} onMouseLeave={handleMouseLeave}>status</span>, 'Active') AS status,
    <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'datetime' ? 'bg-emerald-200 text-emerald-900 font-bold' : 'bg-emerald-100 text-emerald-800 font-semibold'} py-0.5 rounded`} onMouseEnter={() => handleMouseEnter('datetime')} onMouseLeave={handleMouseLeave}>SYSTIMESTAMP</span> AS ts
<span className="text-blue-600 font-semibold">FROM</span> <span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'identifiers' ? 'bg-emerald-200 text-emerald-900 font-bold' : ''}`} onMouseEnter={() => handleMouseEnter('identifiers')} onMouseLeave={handleMouseLeave}>production.users</span>
<span className={`cursor-pointer transition-colors px-0.5 rounded ${activeConstruct === 'paging' ? 'bg-emerald-200 text-emerald-900 font-bold' : 'text-emerald-600 font-semibold'}`} onMouseEnter={() => handleMouseEnter('paging')} onMouseLeave={handleMouseLeave}>FETCH FIRST 100 ROWS ONLY</span>;
</pre>
                </div>
              </div>
            </div>
            
            <div className="p-5 bg-gray-50 border-t border-gray-200 text-[15px] text-gray-600 font-medium flex items-center justify-between flex-wrap gap-4">
              <p className="max-w-3xl leading-relaxed">
                Candor maps dialect constructs (TOP, ISNULL, GETDATE, #temp) against source SQL Server and target Oracle schemas so objects are not invented from a text-only prompt.
              </p>
            </div>
          </div>

          {/* Common mappings footer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div 
               className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${activeConstruct === 'paging' ? 'border-amber-400 bg-amber-50 shadow-md scale-[1.02]' : 'border-gray-300 bg-white shadow-sm hover:shadow-md hover:border-gray-400'}`}
               onMouseEnter={() => handleMouseEnter('paging')} onMouseLeave={handleMouseLeave}
            >
               <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Paging / Limits</div>
               <div className="font-mono text-[13px] text-gray-800"><span className="text-red-500 font-medium">TOP n</span> <span className="text-gray-300 mx-2">→</span> <span className="text-emerald-600 font-medium">FETCH FIRST n ROWS</span></div>
            </div>
            <div 
               className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${activeConstruct === 'datetime' ? 'border-amber-400 bg-amber-50 shadow-md scale-[1.02]' : 'border-gray-300 bg-white shadow-sm hover:shadow-md hover:border-gray-400'}`}
               onMouseEnter={() => handleMouseEnter('datetime')} onMouseLeave={handleMouseLeave}
            >
               <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Datetime</div>
               <div className="font-mono text-[13px] text-gray-800"><span className="text-red-500 font-medium">GETDATE()</span> <span className="text-gray-300 mx-2">→</span> <span className="text-emerald-600 font-medium">SYSTIMESTAMP / SYSDATE</span></div>
            </div>
            <div 
               className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${activeConstruct === 'null' ? 'border-amber-400 bg-amber-50 shadow-md scale-[1.02]' : 'border-gray-300 bg-white shadow-sm hover:shadow-md hover:border-gray-400'}`}
               onMouseEnter={() => handleMouseEnter('null')} onMouseLeave={handleMouseLeave}
            >
               <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Null Handling</div>
               <div className="font-mono text-[13px] text-gray-800"><span className="text-red-500 font-medium">ISNULL()</span> <span className="text-gray-300 mx-2">→</span> <span className="text-emerald-600 font-medium">NVL() / COALESCE()</span></div>
            </div>
            <div 
               className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${activeConstruct === 'temp' ? 'border-amber-400 bg-amber-50 shadow-md scale-[1.02]' : 'border-gray-300 bg-white shadow-sm hover:shadow-md hover:border-gray-400'}`}
               onMouseEnter={() => handleMouseEnter('temp')} onMouseLeave={handleMouseLeave}
            >
               <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Temp Tables</div>
               <div className="font-mono text-[13px] text-gray-800"><span className="text-red-500 font-medium">#temp</span> <span className="text-gray-300 mx-2">→</span> <span className="text-emerald-600 font-medium">Global Temporary Tables</span></div>
            </div>
            <div 
               className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${activeConstruct === 'identifiers' ? 'border-amber-400 bg-amber-50 shadow-md scale-[1.02]' : 'border-gray-300 bg-white shadow-sm hover:shadow-md hover:border-gray-400'}`}
               onMouseEnter={() => handleMouseEnter('identifiers')} onMouseLeave={handleMouseLeave}
            >
               <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Identifiers</div>
               <div className="font-mono text-[13px] text-gray-800"><span className="text-red-500 font-medium">[col]</span> <span className="text-gray-300 mx-2">→</span> <span className="text-emerald-600 font-medium">unquoted / "col"</span></div>
            </div>
            <div 
               className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${activeConstruct === 'auto' ? 'border-amber-400 bg-amber-50 shadow-md scale-[1.02]' : 'border-gray-300 bg-white shadow-sm hover:shadow-md hover:border-gray-400'}`}
               onMouseEnter={() => handleMouseEnter('auto')} onMouseLeave={handleMouseLeave}
            >
               <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Auto-increment</div>
               <div className="font-mono text-[13px] text-gray-800"><span className="text-red-500 font-medium">IDENTITY</span> <span className="text-gray-300 mx-2">→</span> <span className="text-emerald-600 font-medium">sequences / identity</span></div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}
